import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AdminUser } from '../types/admin';

export const useAdminAuth = () => {
  const { user, isAuthenticated } = useAuth();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated || !user) {
        setAdminUser(null);
        setIsAdmin(false);
        setIsClient(false);
        setLoading(false);
        return;
      }

      try {
        // Don't set admin/client status for master users - they use master dashboard
        // Master users should have isAdmin = false and isClient = false
        if (user.role === 'master' || user.isMaster === true) {
          setAdminUser(null);
          setIsAdmin(false);
          setIsClient(false);
          setLoading(false);
          return;
        }

        // Check if user has admin role (not master)
        const isAdminRole = user.role === 'admin';
        const userRole = isAdminRole ? 'admin' : 'client';
        
        const mockAdminUser: AdminUser = {
          id: user.id,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`.trim() || user.email,
          role: userRole,
          clientId: isAdminRole ? undefined : `client_${user.id}`,
          permissions: getPermissionsByRole(userRole),
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
        };

        setAdminUser(mockAdminUser);
        setIsAdmin(mockAdminUser.role === 'admin');
        setIsClient(mockAdminUser.role === 'client');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setAdminUser(null);
        setIsAdmin(false);
        setIsClient(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, user]);

  const hasPermission = (permissionName: string): boolean => {
    if (!adminUser) return false;
    return adminUser.permissions.some(p => p.name === permissionName);
  };

  const hasAnyPermission = (permissionNames: string[]): boolean => {
    if (!adminUser) return false;
    return permissionNames.some(name => hasPermission(name));
  };

  const canAccessAdminDashboard = (): boolean => {
    return isAdmin || isClient;
  };

  const canManageAllEvents = (): boolean => {
    return isAdmin;
  };

  const canManageOwnEvents = (): boolean => {
    return isClient || isAdmin;
  };

  const canViewAnalytics = (): boolean => {
    return hasPermission('view_analytics') || isAdmin;
  };

  const canExportData = (): boolean => {
    return hasPermission('export_data') || isAdmin;
  };

  return {
    adminUser,
    isAdmin,
    isClient,
    loading,
    hasPermission,
    hasAnyPermission,
    canAccessAdminDashboard,
    canManageAllEvents,
    canManageOwnEvents,
    canViewAnalytics,
    canExportData,
  };
};

const getPermissionsByRole = (role: 'admin' | 'client') => {
  const adminPermissions = [
    { id: '1', name: 'manage_all_events', description: 'Manage all events', category: 'events' as const },
    { id: '2', name: 'manage_users', description: 'Manage users', category: 'users' as const },
    { id: '3', name: 'view_all_analytics', description: 'View all analytics', category: 'analytics' as const },
    { id: '4', name: 'export_data', description: 'Export data', category: 'analytics' as const },
    { id: '5', name: 'manage_settings', description: 'Manage settings', category: 'settings' as const },
  ];

  const clientPermissions = [
    { id: '6', name: 'manage_own_events', description: 'Manage own events', category: 'events' as const },
    { id: '7', name: 'view_analytics', description: 'View analytics', category: 'analytics' as const },
    { id: '8', name: 'export_data', description: 'Export data', category: 'analytics' as const },
    { id: '9', name: 'view_registrations', description: 'View registrations', category: 'tickets' as const },
  ];

  return role === 'admin' ? adminPermissions : clientPermissions;
}; 